import { useEffect, useRef, useState } from "react";

function prefersReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Animates a number from 0 → target with an ease-out curve.
 * Returns the target immediately when the user prefers reduced motion.
 */
export function useCountUp(target: number, duration = 900): number {
  const safeTarget = Number.isFinite(target) ? target : 0;
  const [value, setValue] = useState(() => (prefersReducedMotion() ? safeTarget : 0));
  const frame = useRef<number>();

  useEffect(() => {
    if (prefersReducedMotion()) {
      setValue(safeTarget);
      return;
    }
    const start = performance.now();
    const from = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(from + (safeTarget - from) * eased);
      if (t < 1) frame.current = requestAnimationFrame(tick);
    };
    frame.current = requestAnimationFrame(tick);
    return () => {
      if (frame.current) cancelAnimationFrame(frame.current);
    };
  }, [safeTarget, duration]);

  return value;
}

/** Count-up that preserves non-numeric display values (e.g. "12%", "—"). */
export function useCountUpDisplay(display: string | number, duration = 900): string {
  const raw = String(display ?? "");
  const match = raw.match(/-?\d+(\.\d+)?/);
  const numeric = match ? Number(match[0]) : NaN;
  const animated = useCountUp(Number.isFinite(numeric) ? numeric : 0, duration);
  if (!match || !Number.isFinite(numeric)) return raw;
  const decimals = match[0].includes(".") ? match[0].split(".")[1].length : 0;
  return raw.replace(match[0], animated.toFixed(decimals));
}
